import dayjs from 'dayjs'
import { ContactSource } from '../types/response'

export type LeadAlertType = 'unassigned' | 'stale' | null

export interface LeadAlert {
    type: LeadAlertType
    message: string
    daysOverdue: number
}

const LEAD_ALERT_THRESHOLD_HOURS = 24

/**
 * Check if a lead needs alert highlighting
 * - unassigned: New lead without assignee for >= 24 hours (from created_at)
 * - stale: Assigned lead not classified for >= 24 hours (from assigned_at)
 */
export const getLeadAlert = (contact: ContactSource): LeadAlert | null => {
    const now = dayjs()
    const createdAt = dayjs(contact.contact_source_created_at)
    const assignedAt = contact.contact_source_assigned_at
        ? dayjs(contact.contact_source_assigned_at)
        : null
    const status = contact.contact_source_status
    const assigneeIds = contact.contact_source_assignee_ids || []

    // Skip if already classified, converted, or spam
    const classifiedStatuses = [
        'warm-lead',
        'sale-qualified-lead',
        'cool-lead',
        'un-qualified-lead',
        'MATCHED',
        'SPAM',
        'HR',
    ]
    if (classifiedStatuses.includes(status || '')) {
        return null
    }

    // Check unassigned >= 24 hours (from created_at)
    const hoursSinceCreated = now.diff(createdAt, 'hour')
    const daysSinceCreated = Math.floor(hoursSinceCreated / 24)
    if (
        assigneeIds.length === 0 &&
        hoursSinceCreated >= LEAD_ALERT_THRESHOLD_HOURS
    ) {
        return {
            type: 'unassigned',
            message: 'lead-alert-unassigned',
            daysOverdue: daysSinceCreated,
        }
    }

    // Check assigned but not classified >= 24 hours (from assigned_at)
    if (assigneeIds.length > 0 && assignedAt) {
        const hoursSinceAssigned = now.diff(assignedAt, 'hour')
        const daysSinceAssigned = Math.floor(hoursSinceAssigned / 24)
        if (hoursSinceAssigned >= LEAD_ALERT_THRESHOLD_HOURS) {
            const unclassifiedStatuses = [
                'UNVERIFIED',
                'UN-MATCHED',
                'FOLLOWING',
                'UNCLASSIFIED',
                '',
            ]
            if (unclassifiedStatuses.includes(status || '')) {
                return {
                    type: 'stale',
                    message: 'lead-alert-stale',
                    daysOverdue: daysSinceAssigned,
                }
            }
        }
    }

    return null
}

/**
 * Get alert color classes based on alert type
 */
export const getAlertColors = (type: LeadAlertType) => {
    switch (type) {
        case 'unassigned':
            return {
                bg: 'bg-orange-50',
                border: 'border-orange-300',
                text: 'text-orange-700',
                icon: 'text-orange-500',
            }
        case 'stale':
            return {
                bg: 'bg-red-50',
                border: 'border-red-300',
                text: 'text-red-700',
                icon: 'text-red-500',
            }
        default:
            return null
    }
}
